import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { z } from "zod";

import { isDeepEqual } from "@punto/frontend-shared/utils/is-equal";

interface UseSearchParamsStateConfig<T> {
  defaultState: T;
}

export interface SearchParamsStateChangeOptions {
  /**
   * Amend the current history entry instead of adding one. For a write the user
   * did not perform — reflecting an already-applied filter back into the URL —
   * where a new entry would give Back something meaningless to land on.
   */
  replace?: boolean;
}

/**
 * A state as `?state=` holds it once serialized and read back. A JSON round
 * trip, not `structuredClone`: what counts is what JSON drops, such as
 * `undefined`-valued keys.
 */
const asStoredInUrl = (state: unknown): unknown => {
  if (state === undefined) return state;

  const serialized = JSON.stringify(state);
  return JSON.parse(serialized);
};

/**
 * Table state in the URL — written only by `handleChange`, never from an effect
 * inside this hook.
 *
 * `?state=` used to be mirrored here on every render, which made it flicker in
 * and out while switching views: on the first render after a switch the session
 * value still belonged to the *previous* view, so the mirror pushed that stale
 * state onto the new view's URL and removed it a render later — leaving a history
 * entry Back would land on, carrying filters the user never applied. Deciding
 * *what* deserves to be in the URL now belongs to the caller, which is the only
 * layer that knows whether a state is a real applied tweak; see `useTableState`.
 */
/**
 * The params `current` should become once `state` is applied — the whole
 * serialization decision in one pure function, so it can be reasoned about (and
 * tested) without a router.
 *
 * Built off the live params so anything this hook does not own — Portus's
 * `?customer=` scope — is carried through rather than dropped. Back at the
 * default, `state` is deleted instead of serialized: the URL should read as
 * "nothing applied", not as an explicit copy of the defaults.
 *
 * Exported because this module is the one place that knows table state lives
 * under `?state=`, and the URL sink has two writers: `handleChange` below, and
 * `useApplyTableState` for a state handed to a list being navigated to. Nothing
 * else may serialize a state — an application that concatenates
 * `?state=${encodeURIComponent(...)}` itself has copied both the param name and
 * the "equals the default → drop it" rule, and only one of those copies gets
 * updated (P-1585).
 *
 * "Back at the default" is judged on the state as the URL stores it: JSON drops
 * `undefined`-valued keys, so a filter patched to `undefined` (P-2926: clearing
 * the Cliente filter's organizations) cannot make a state that reads back as
 * the default write a `?state=`.
 */
export const applyStateToSearchParams = <T,>(
  current: URLSearchParams,
  state: T,
  defaultState: T,
): URLSearchParams => {
  const next = new URLSearchParams(current);

  if (isDeepEqual(asStoredInUrl(defaultState), asStoredInUrl(state))) {
    next.delete("state");
  } else {
    next.set("state", JSON.stringify(state));
  }

  return next;
};

export function useSearchParamsState<T>(
  initialState: T,
  schema: z.ZodSchema<T>,
  config: UseSearchParamsStateConfig<T>,
) {
  const { defaultState } = config;
  const [searchParams, setSearchParams] = useSearchParams();

  const state: T = useMemo(() => {
    try {
      const parsed = JSON.parse(searchParams.get("state") ?? "") as T;
      return schema.parse(parsed);
    } catch {
      return initialState;
    }
  }, [searchParams, initialState, schema]);

  const handleChange = useCallback(
    (newPartialState: Partial<T>, options?: SearchParamsStateChangeOptions) => {
      const newState = { ...state, ...newPartialState };

      const next = applyStateToSearchParams(
        searchParams,
        newState,
        defaultState,
      );

      // `setSearchParams` navigates unconditionally, so a write that changes
      // nothing still costs a history entry. Skipping those is what keeps Back
      // meaningful: one entry per actual change.
      if (next.toString() === searchParams.toString()) return;

      setSearchParams(next, { replace: options?.replace });
    },
    [state, searchParams, setSearchParams, defaultState],
  );

  return [state, handleChange] as const;
}
